// Zod schemas for the YouTube Data API v3 video shape + our local grouping override.
// youtube.json is auto-generated by the fetcher; video-groups.json is editable
// (controls which playlist group a video shows in, and any custom thumbnail).

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Raw YouTube videos.list response item — we only model the fields we actually
// read. Extra fields are allowed but ignored.
// ---------------------------------------------------------------------------

const YouTubeThumbnailSchema = z.object({
  url: z.string().url(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

const YouTubeThumbnailsSchema = z.object({
  default: YouTubeThumbnailSchema.optional(),
  medium: YouTubeThumbnailSchema.optional(),
  high: YouTubeThumbnailSchema.optional(),
  standard: YouTubeThumbnailSchema.optional(),
  maxres: YouTubeThumbnailSchema.optional(),
});

const YouTubeVideoSnippetSchema = z.object({
  publishedAt: z.string(),
  channelId: z.string(),
  title: z.string(),
  description: z.string().default(''),
  channelTitle: z.string().default(''),
  categoryId: z.string().optional(),
  tags: z.array(z.string()).default([]),
  thumbnails: YouTubeThumbnailsSchema.optional(),
});

const YouTubeVideoStatsSchema = z.object({
  viewCount: z.string().optional(),
  likeCount: z.string().optional(),
  commentCount: z.string().optional(),
});

const YouTubeVideoContentDetailsSchema = z.object({
  duration: z.string().default('PT0S'),
});

export const RawYouTubeVideoSchema = z.object({
  id: z.string(),
  snippet: YouTubeVideoSnippetSchema,
  statistics: YouTubeVideoStatsSchema.optional(),
  contentDetails: YouTubeVideoContentDetailsSchema.optional(),
});

export const RawYouTubeVideosSchema = z.array(RawYouTubeVideoSchema);

export type RawYouTubeVideo = z.infer<typeof RawYouTubeVideoSchema>;

// ---------------------------------------------------------------------------
// Normalised shape — what the UI actually consumes. Stripped, flat, typed.
// ---------------------------------------------------------------------------

export const VideoSchema = z.object({
  videoId: z.string(),
  title: z.string(),
  description: z.string(),
  channelTitle: z.string(),
  channelId: z.string(),
  publishedAt: z.string(),           // ISO date
  year: z.string(),                  // e.g. "2023"
  duration: z.string(),              // ISO 8601 e.g. "PT49M20S"
  durationLabel: z.string(),         // "49:20"
  durationSeconds: z.number().int().nonnegative(),
  viewCount: z.number().int().nonnegative(),
  likeCount: z.number().int().nonnegative(),
  tags: z.array(z.string()),
  thumbnailUrl: z.string().optional(),
  url: z.string(),
});

export type Video = z.infer<typeof VideoSchema>;

// ---------------------------------------------------------------------------
// video-groups.json — local editorial overlay.
// Maps videoId → which playlist group + optional custom branded image.
// ---------------------------------------------------------------------------

/** Per-video tile metadata used to generate branded artwork. */
export const VideoTileMetaSchema = z.object({
  /** Big headline shown on the tile. Keep punchy: 3-5 words ideal. */
  title: z.string(),
  /** Subtitle below the title — single line, ~80 chars max. */
  subtitle: z.string(),
  /** Bottom-strip eyebrow — auto ALL-CAPS at render time. */
  eyebrow: z.string(),
});

export const VideoGroupAssignmentSchema = z.object({
  /** Playlist title the video should appear under. */
  group: z.string(),
  /** Curated tile metadata. If absent, defaults derived from YouTube fields. */
  tile: VideoTileMetaSchema.optional(),
  /** Optional path or URL to a custom thumbnail image (relative path = public/). */
  customThumbnail: z.string().optional(),
});

export type VideoTileMeta = z.infer<typeof VideoTileMetaSchema>;

export const VideoGroupsMapSchema = z.object({
  /** Ordered list of group titles. Drives the order on the page. */
  order: z.array(z.string()).min(1),
  /** Fallback group for any videoId not explicitly listed in `assignments`. */
  defaultGroup: z.string(),
  /** Map of videoId → assignment. Missing IDs fall back to defaultGroup. */
  assignments: z.record(z.string(), VideoGroupAssignmentSchema),
});

export type VideoGroupsMap = z.infer<typeof VideoGroupsMapSchema>;
export type VideoGroupAssignment = z.infer<typeof VideoGroupAssignmentSchema>;
